import { createAction, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import { push } from 'react-router-redux';

import { map, filter, switchMap, debounceTime } from 'rxjs/operators';

import { useRedux } from 'util/hook/redux';
import { wrapAuthFetch, wrapFetch } from 'util/api';
import { isEmpty, isExist, mapValueFromServer } from 'util/helper';
import storage from 'util/storage';
import { sendAddToCart, sendInitiateCheckout } from 'util/analytics';

import { getContact } from './contact';
import { openWarningAlert, openNormalAlert } from './alert';
import { updateProduct, isProductCanUseBonus } from './product';

export const defaultCartProduct = {
	id: '',
	productId: -1,
	no: '',
	count: 0,
	checked: true,
};

export const defaultStatusData = {
	items: [],
	user: {
		name: '',
		email: '',
		mobile: '',
		zip_code: 0,
		city: '',
		district: '',
		address: '',
		credit: 0,
	},
	installment: false,
	rewards: [],
	deliveries: [],
	coupon: [],
	max_credit: 0,
};

const toggleProductInCart = createAction('TOGGLE_PRODUCT_IN_CART', (id, no) => (_, getState) => {
	const productNo = `${id}_${no}`;

	const {
		cart: { data, additional },
	} = getState();

	const productData = {
		[productNo]: {
			...data[productNo],
			checked: !data[productNo].checked,
		},
	};

	const additionalList = Object.keys(additional);

	const productAdditionalList = additionalList.filter(
		additionalId => additional[additionalId].parentId === id,
	);

	const newAdditionalData = {};

	productAdditionalList.forEach(additionalId => {
		newAdditionalData[additionalId] = {
			...additional[additionalId],
			checked: false,
		};
	});

	return {
		productData,
		additional: newAdditionalData,
	};
});

const addProductIntoCart = createAction('ADD_PRODUCT_INTO_CART', (id, no, count) => {
	// TODO: content_name, contents, currency, value
	sendAddToCart({ content_ids: id, content_type: 'product' });

	return {
		id: `${id}_${no}`,
		productId: id,
		no,
		count,
		checked: true,
	};
});

// 加價購商品的 action

const addAdditionalIntoCart = createAction(
	'ADD_ADDITIONAL_INTO_CART',
	(id, no, count, stockData, parentId) => ({
		id: `${id}_${no}`,
		productId: id,
		no,
		count,
		stockData,
		parentId,
		checked: true,
	}),
);

const addAdditionalIntoCartProcess = createAction(
	'ADD_ADDITIONAL_INTO_CART_PROCESS',
	(id, no, count, stockData, parentId, goToCart = false) => dispatch => {
		if (isEmpty(id) || isEmpty(no) || isEmpty(count) || count === 0) {
			dispatch(openWarningAlert('請先選擇顏色及數量'));
			return;
		}

		dispatch(addAdditionalIntoCart(id, no, count, stockData, parentId));
		dispatch(openNormalAlert('商品已加入 購物車'));

		if (goToCart) {
			dispatch(push('/cart'));
		}
	},
);

const toggleAdditionalInCart = createAction(
	'TOGGLE_ADDITIONAL_IN_CART',
	(id, no) => (_, getState) => {
		const {
			cart: { data, additional },
		} = getState();
		const additionalId = `${id}_${no}`;

		const mainProduct = Object.values(data).find(
			item => item.productId === additional[additionalId].parentId,
		);
		let newAdditionalData = {};
		if (mainProduct.checked) {
			newAdditionalData = {
				...additional,
				[additionalId]: {
					...additional[additionalId],
					checked: !additional[additionalId].checked,
				},
			};
		} else if (!mainProduct.checked) {
			newAdditionalData = {
				...additional,
				[additionalId]: {
					...additional[additionalId],
					checked: false,
				},
			};
		}

		return {
			additional: newAdditionalData,
		};
	},
);

const updateAdditionalCount = createAction('UPDATE_ADDITIONAL_COUNT', (id, count) => ({
	id,
	count,
}));

const removeAdditionalFromCart = createAction(
	'REMOVE_ADDITIONAL_FROM_CART',
	(id, no) => `${id}_${no}`,
);

export const addAdditionalIntoCartEpic = (action$, state$) =>
	action$.pipe(
		filter(
			action =>
				action.type === 'ADD_ADDITIONAL_INTO_CART' ||
				action.type === 'UPDATE_ADDITIONAL_COUNT' ||
				action.type === 'TOGGLE_ADDITIONAL_IN_CART' ||
				action.type === 'REMOVE_ADDITIONAL_FROM_CART' ||
				action.type === 'REMOVE_PRODUCT_FROM_CART' ||
				action.type === 'UPDATE_ADDITIONAL_PRODUCT',
		),
		map(() => {
			const { cart } = state$.value;

			storage.setItem('cartAdditional', JSON.stringify(cart.additional));

			return { type: 'SYNC_CART_TO_LOCALSTORAGE' };
		}),
	);

export const addProductIntoCartEpic = (action$, state$) =>
	action$.pipe(
		filter(
			action =>
				action.type === 'ADD_PRODUCT_INTO_CART' ||
				action.type === 'UPDATE_PRODUCT_COUNT' ||
				action.type === 'TOGGLE_PRODUCT_IN_CART' ||
				action.type === 'REMOVE_PRODUCT_FROM_CART' ||
				action.type === 'UPDATE_CART_PRODUCT_FULFILLED',
		),
		map(() => {
			const { cart, product } = state$.value;

			storage.setItem('cartData', JSON.stringify(cart.data));
			storage.setItem('cartList', JSON.stringify(cart.list));

			const needCacheProduct = {};
			cart.list.forEach(cartId => {
				needCacheProduct[cart.data[cartId].productId] = product.data[cart.data[cartId].productId];
			});

			storage.setItem('productData', JSON.stringify(needCacheProduct));

			return { type: 'SYNC_CART_TO_LOCALSTORAGE' };
		}),
	);

export const clearCart = createAction('CLEAR_CART', () => {
	storage.setItem('cartData', JSON.stringify({}));
	storage.setItem('cartList', JSON.stringify([]));
	storage.setItem('productData', JSON.stringify({}));
	storage.setItem('cartAdditional', JSON.stringify({}));
});

export const loadCartDataFromLocalstorage = createAction('LOAD_CART_DATA_FROM_LOCALSTORAGE', () => {
	const carData = JSON.parse(storage.getItem('cartData')) || {};
	const carList = JSON.parse(storage.getItem('cartList')) || [];
	const productData = JSON.parse(storage.getItem('productData')) || {};
	const additionalData = JSON.parse(storage.getItem('cartAdditional')) || {};

	return { carData, carList, productData, additionalData };
});

const addProductIntoCartProcess = createAction(
	'ADD_PRODUCT_INTO_CART_PROCESS',
	(id, no, count, goToCart = false) => dispatch => {
		if (isEmpty(id) || isEmpty(no) || isEmpty(count) || count === 0) {
			dispatch(openWarningAlert('請先選擇顏色及數量'));
			return;
		}

		dispatch(addProductIntoCart(id, no, count));
		dispatch(openNormalAlert('商品已加入 購物車'));

		if (goToCart) {
			dispatch(push('/cart'));
		}
	},
);

const updateProductCount = createAction('UPDATE_PRODUCT_COUNT', (id, count) => ({
	id,
	count,
}));

const removeProductFromCart = createAction('REMOVE_PRODUCT_FROM_CART', (id, no) => ({
	id,
	no: `${id}_${no}`,
}));

const nextCartStep = createAction('NEXT_CART_STEP');
const prevCartStep = createAction('PREV_CART_STEP');
export const setupCartStep = createAction('SETUP_CART_STEUP', step => ({ step }));

export const resetCartData = createAction('RESET_CART_DATA');

const selectCartProduct = createSelector(
	state => state.cart.data,
	state => state.cart.list,
	state => state.cart.additional,
	(data, list, additional) => ({ data, list, additional }),
);

const selectAdditionalData = createSelector(
	state => state.cart.additional,
	additionalInCart => ({ additionalInCart }),
);

const defaultCouponData = {
	data: [],
};

export const getCouponListInCart = createAction(
	'GET_COUPON_LIST_IN_CART',
	() => async (dispatch, getState) => {
		const {
			cart: { data: cartData, list, additional },
		} = getState();

		const productData = list
			.map(id => cartData[id])
			.filter(product => product.checked)
			.map(product => {
				const cartAdditional = Object.values(additional)
					.filter(checkAdd => checkAdd.checked && checkAdd.parentId === product.productId)
					.map(item => ({
						id: item.productId,
						quantity: item.count,
						no: item.no,
					}));

				return {
					increase_purchases: cartAdditional,
					no: product.no,
					quantity: product.count,
					product_id: parseInt(product.productId, 10),
				};
			});

		const { status, data } = await wrapAuthFetch('cart/ec-coupons', {
			method: 'POST',
			body: JSON.stringify({ products: productData }),
		});

		if (status !== 200) {
			return { data: { defaultCouponData } };
		}

		return { data };
	},
);

export const calculateAdditionalPrice = createSelector(
	state => state.cart.additional,
	additionalPrice => {
		const price = Object.values(additionalPrice)
			.filter(item => item.checked)
			.map(item => item.stockData.price * item.count);
		const reducer = (accum, current) => accum + current;
		const totalAdditionalPrice = price.reduce(reducer, 0);

		return totalAdditionalPrice;
	},
);

export const calculateTotalPrice = createSelector(
	state => state.cart.data,
	state => state.cart.list,
	state => state.product.data,
	(cartData, cartList, product) =>
		cartList
			.filter(cartId => cartData[cartId].checked)
			.map(cartId => cartData[cartId])
			.reduce((prev, { productId, count }) => {
				const productPrice = product[productId].discount
					? product[productId].discount
					: product[productId].price;

				const total = productPrice * count;

				return prev + total;
			}, 0),
);

const updateAdditionalProduct = createAction(
	'UPDATE_ADDITIONAL_PRODUCT',
	(productId, parentId, data) => (_, getState) => {
		const {
			cart: { additional },
		} = getState();

		const additionalData = {
			name: data.name,
			categories: data.categories[0] ? data.categories[0].name : '',
			price: data.actual_price,
			parentId: `${data.increase_purchase_parent_product_id}`,
			details: data.stock,
		};

		const id = Object.keys(additional).find(
			additionalId =>
				additional[additionalId].productId === productId &&
				additional[additionalId].parentId === parentId,
		);

		return { id, additionalData };
	},
);

export const updateCartProduct = createAction(
	'UPDATE_CART_PRODUCT',
	() => async (dispatch, getState) => {
		const {
			cart: { data: cartData, list, additional },
		} = getState();

		const cartProduct = list
			.map(id => cartData[id])
			.map(product => {
				const cartAdditional = Object.values(additional)
					.filter(checkAdd => checkAdd.parentId === product.productId)
					.map(item => ({
						id: item.productId,
						quantity: item.count,
						no: item.no,
					}));

				return {
					increase_purchases: cartAdditional,
					no: product.no,
					quantity: product.count,
					product_id: parseInt(product.productId, 10),
				};
			});
		const { data } = await wrapFetch('cart', {
			method: 'POST',
			body: JSON.stringify({ products: cartProduct }),
		});

		if (data) {
			data.items.forEach(item => {
				if (!item.increase_purchase_parent_product_id) {
					const canUseBonus = isProductCanUseBonus(item);
					dispatch(updateProduct(item.id, { ...item, canUseBonus }));
				} else {
					dispatch(
						updateAdditionalProduct(item.id, item.increase_purchase_parent_product_id, ...item),
					);
				}
			});
		}
	},
);

const updateShipSelect = createAction('UPDATE_SHIP_SELECT', (key, value) => ({
	key,
	value,
}));

const cleanShipSelect = createAction('CLEAN_SHIP_SELECT');

export const updateShipValue = createAction('UPDATE_SHIP_VALUE', (key, value) => ({
	key,
	value,
}));

const jumpOnPaymentStep = createAction('JUMP_ON_PAYMENT_STEP', step => ({
	paymentStep: step,
}));

const defaultDelivery = {
	id: -1,
	name: '',
	content: '',
	price: 0,
	type: '',
};

export const getDiscountRulePrice = createSelector(
	state => state.cart.data,
	state => state.cart.list,
	state => state.product.data,
	(cartData, cartList, product) => {
		const checkedProductDiscountRulePrice = cartList
			.filter(cartId => cartData[cartId].checked)
			.map(cardId => product[cartData[cardId].productId])
			.filter(checkedProduct => checkedProduct.canUseBonus)
			.map(discountableProduct => discountableProduct.discount_rule.price);
		return checkedProductDiscountRulePrice;
	},
);

export const selectDelivery = createSelector(
	state => state.cart.delivery,
	state => state.cart.statusData.deliveries,
	(delivery, deliveries) =>
		deliveries.filter(item => item.id === delivery.select)[0] || defaultDelivery,
);

export const selectPromotion = createSelector(
	state => state.cart.promotion,
	promotion => {
		const freeGifts = promotion
			.filter(item => item.type === 'TARGET_NUMBER')
			.map(item => ({
				...item,
				free_gift: item.free_gift.map(fg => ({ ...fg, quantity: item.quantity })),
			}))
			.flatMap(item => item.free_gift);
		const discount = promotion.filter(item => item.type !== 'TARGET_NUMBER');
		const quantity =
			promotion.map(item => item.quantity).length > 0 ? promotion.map(item => item.quantity)[0] : 0;

		return { promotion, freeGifts, discount, quantity };
	},
);

const calculateTotalPriceWithStep1 = createSelector(
	calculateAdditionalPrice,
	calculateTotalPrice,
	selectPromotion,
	(additional, total, promotion) => {
		const resultPromotion =
			promotion.discount.length > 0 ? Math.trunc(promotion.discount[0].discount) : 0;
		return additional + total - resultPromotion;
	},
);

const calculateTotalPriceForBonus = createSelector(
	calculateTotalPrice,
	selectPromotion,
	calculateAdditionalPrice,
	(totalPrice, promotion, additional) => {
		const resultPromotion =
			promotion.discount.length > 0 ? Math.trunc(promotion.discount[0].discount) : 0;
		return totalPrice - resultPromotion + additional;
	},
);

const checkCartStatus = createAction('CHECK_CART_STATUS', () => async (dispatch, getState) => {
	const {
		cart: { data: cartData, list, additional },
	} = getState();

	const checkData = list
		.map(id => cartData[id])
		.filter(product => product.checked)
		.map(product => {
			const cartAdditional = Object.values(additional)
				.filter(checkAdd => checkAdd.checked && checkAdd.parentId === product.productId)
				.map(item => ({
					id: item.productId,
					quantity: item.count,
					no: item.no,
				}));

			return {
				increase_purchases: cartAdditional,
				no: product.no,
				quantity: product.count,
				product_id: parseInt(product.productId, 10),
			};
		});

	const { status, data } = await wrapAuthFetch('user/cart', {
		method: 'POST',
		body: JSON.stringify({ products: checkData }),
	});

	if (status !== 200) {
		return defaultStatusData;
	}

	const productCanUseBonus = data.items
		.filter(item => !item.increase_purchase_parent_product_id)
		.filter(item => isProductCanUseBonus(item));
	const totalPriceForBonus = calculateTotalPriceForBonus(getState()); // 總額

	const promotion = selectPromotion(getState());
	// 可使用折扣之商品 id
	const promotionRestrictedId =
		promotion.discount.length > 0 && promotion.discount[0].restricted_product_ids
			? promotion.discount[0].restricted_product_ids
			: [];

	const maxDiscountItem = productCanUseBonus.reduce((maxItem, currentItem) => {
		if (!maxItem || currentItem.max_discount_percentage > maxItem.max_discount_percentage) {
			if (promotionRestrictedId.includes(currentItem.id)) {
				return currentItem;
			}
			if (promotion.discount[0] && !promotion.discount[0].restricted_product_ids) {
				return currentItem;
			}
		}
		return maxItem;
	}, null);

	const maxCredit = productCanUseBonus
		.filter(item => item.discount_rule.price <= totalPriceForBonus)
		.map(item => {
			const listId = list.find(id => id.split('_')[0] === item.id.toString());
			if (listId) {
				if (maxDiscountItem && listId.split('_')[0] === maxDiscountItem.id.toString()) {
					// 將折扣扣在紅利折抵%數最高商品

					const promotionPrice = promotion.discount.length > 0 ? promotion.discount[0].discount : 0;
					return (
						Math.trunc(
							(item.actual_price - promotionPrice) * (item.max_discount_percentage / 100),
						) +
						Math.trunc(
							(cartData[listId].count - 1) *
								item.actual_price *
								(item.max_discount_percentage / 100),
						)
					);
				}

				return Math.trunc(
					cartData[listId].count * item.actual_price * (item.max_discount_percentage / 100),
				);
			}
			return 0;
		})
		.reduce((accDiscount, currDiscount) => accDiscount + currDiscount, 0);
	const maxBonusDiscount = data.user.credit < maxCredit ? data.user.credit : maxCredit;

	const notEnoughProduct = data.items.filter(item => !item.enough);

	if (notEnoughProduct.length > 0) {
		const notEnoughProductAlert = notEnoughProduct
			.map(item => `商品：${item.name} 庫存不夠，請再次確認`)
			.join('\n');

		dispatch(openWarningAlert(notEnoughProductAlert));
		return { ...data, maxBonusDiscount };
	}

	const checkDataLength =
		list.map(id => cartData[id]).filter(product => product.checked).length +
		Object.values(additional).filter(checkAdd => checkAdd.checked).length;

	if (data.items.length !== checkDataLength) {
		const noExistProduct = checkData.filter(
			item => !data.items.some(checkItem => checkItem.id === item.product_id),
		);

		const noExistProductAlert = noExistProduct
			.map(item => `商品：${item.no} 已下架，請再次確認`)
			.join('\n');

		dispatch(openWarningAlert(noExistProductAlert));
		return { ...data, maxBonusDiscount };
	}

	dispatch(getCouponListInCart());
	// TODO: content_name, contents, currency, value
	sendInitiateCheckout({
		content_ids: checkData.map(item => item.product_id),
		content_type: 'product_group',
	});

	dispatch(nextCartStep());

	return { ...data, maxBonusDiscount };
});

const calculateTotalPriceWithThreshold = createSelector(
	calculateTotalPrice,
	state => state.cart.dividend,
	(totalPrice, dividend) => {
		const resultDividend =
			isEmpty(dividend.select) || isEmpty(dividend.value) ? 0 : parseInt(dividend.value, 10);
		return totalPrice - resultDividend;
	},
);

const calculatePriceWithCoupon = (
	totalPrice,
	type,
	discount,
	couponChecked,
	restrictIds,
	products,
	list,
	cartData,
	additional,
	promotion,
) => {
	const isChecked = !isEmpty(couponChecked.select);
	// 優惠活動折抵金額
	const discountEvent = promotion.discount.length > 0 ? promotion.discount[0] : null;
	const resultPromotion = discountEvent ? Math.trunc(discountEvent.discount) : 0;
	switch (type) {
		case 'PRICE':
			if (isChecked) {
				return totalPrice - resultPromotion - discount + additional;
			}
			return totalPrice - resultPromotion + additional;
		case 'PERCENTAGE':
			if (isChecked) {
				if (!Array.isArray(restrictIds) || !restrictIds.length) {
					const diffPrice = Math.trunc((totalPrice - resultPromotion) * discount);
					return totalPrice - resultPromotion - diffPrice + additional;
				}

				let diffPrice = 0;
				// 適用折扣活動的商品，只有target_number_discount(指定商品滿X件折Y元)才有限制使用商品，target_price(總金額滿X折Y)為全商品通用
				const discountRestrictedIds = discountEvent ? discountEvent.restricted_product_ids : [];
				// 符合折扣活動的商品中，紅利趴數最高的商品，若無限定商品，則不過濾商品
				const maxDiscountItem = products
					.filter(p =>
						!discountRestrictedIds.length ? true : discountRestrictedIds.some(id => id === p.id),
					)
					.reduce((maxItem, currentItem) => {
						if (!maxItem || currentItem.max_discount_percentage > maxItem.max_discount_percentage) {
							return currentItem;
						}
						return maxItem;
					}, null);

				products.forEach(p => {
					let productUseDiscount = false;
					// 判斷此商品是否使用折扣活動

					if (
						(!discountRestrictedIds.length || discountRestrictedIds.includes(p.id)) &&
						p.id === maxDiscountItem.id
					) {
						productUseDiscount = true;
					}

					// 符合優惠券限制商品且此商品有使用折扣活動，則需要扣除優惠折扣金額再乘上優惠券折扣趴數
					if (restrictIds.some(id => id === p.id) && !p.increase_purchase_parent_product_id) {
						const listId = list.find(id => id.split('_')[0] === p.id.toString());
						if (listId) {
							if (productUseDiscount) {
								diffPrice += Math.trunc(
									// eslint-disable-next-line no-mixed-operators
									(p.actual_price * cartData[listId].count - resultPromotion) * discount,
								);
							} else {
								diffPrice += Math.trunc(p.actual_price * cartData[listId].count * discount);
							}
						}
					}
				});
				return totalPrice - resultPromotion - diffPrice + additional;
			}
			return totalPrice - resultPromotion + additional;
		default:
			return totalPrice - resultPromotion + additional;
	}
};

const calculateTotalPriceWithCoupon = createSelector(
	calculateTotalPriceWithThreshold,
	state => state.cart.coupon.value.type,
	state => state.cart.coupon.value.discount,
	state => state.cart.coupon,
	state => state.cart.coupon.value.products,
	state => state.cart.statusData.items,
	state => state.cart.list,
	state => state.cart.data,
	calculateAdditionalPrice,
	selectPromotion,
	(
		totalPrice,
		type,
		discount,
		couponChecked,
		restrictIds,
		products,
		list,
		cartData,
		additional,
		promotion,
	) =>
		calculatePriceWithCoupon(
			totalPrice,
			type,
			discount,
			couponChecked,
			restrictIds,
			products,
			list,
			cartData,
			additional,
			promotion,
		),
);

const calculatePriceWithCouponForCumulativeBonus = createSelector(
	calculateTotalPriceWithThreshold,
	state => state.cart.coupon.value.type,
	state => state.cart.coupon.value.discount,
	state => state.cart.coupon,
	state => state.cart.coupon.value.products,
	state => state.cart.statusData.items,
	state => state.cart.list,
	state => state.cart.data,
	calculateAdditionalPrice,
	selectPromotion,
	(
		totalPrice,
		type,
		discount,
		couponChecked,
		restrictIds,
		products,
		list,
		cartData,
		additional,
		promotion,
	) => {
		const productsWithoutAdditional = products.filter(p => !p.increase_purchase_parent_product_id);
		const notCreditalbeProduct = productsWithoutAdditional.filter(
			p => p.is_creditable === 'DISABLED',
		);
		const creditableProduct = productsWithoutAdditional.filter(p => p.is_creditable === 'ACTIVE');
		const totalPriceWithCreditable =
			totalPrice -
			notCreditalbeProduct
				.map(p => Object.values(cartData).find(q => q.productId === p.id).count * p.actual_price)
				.reduce((prev, curr) => prev + curr, 0);

		return calculatePriceWithCoupon(
			totalPriceWithCreditable,
			type,
			discount,
			couponChecked,
			restrictIds,
			creditableProduct,
			list,
			cartData,
			additional,
			promotion,
		);
	},
);

const calculateTotalPriceForDelivery = createSelector(
	calculateTotalPriceWithCoupon,
	totalPriceWithoutDelivery => totalPriceWithoutDelivery,
);

const selectCartShip = createSelector(
	selectDelivery,
	state => state.cart.payment,
	state => state.cart.dividend,
	state => state.cart.coupon,
	calculateTotalPriceForDelivery,
	(delivery, payment, dividend, coupon, totalPriceForDelivery) => ({
		delivery: { ...delivery, resultPrice: totalPriceForDelivery > 1000 ? 0 : delivery.price },
		payment,
		dividend,
		coupon,
	}),
);

const calculateTotalPriceWithStep2 = createSelector(
	calculateTotalPriceWithCoupon,
	selectCartShip,
	(totalPriceWithoutDelivery, cartShip) =>
		totalPriceWithoutDelivery + cartShip.delivery.resultPrice,
);

const calculatePricenForCumulativeBonus = createSelector(
	calculatePriceWithCouponForCumulativeBonus,
	selectCartShip,
	(totalPriceWithoutDelivery, cartShip) =>
		totalPriceWithoutDelivery + cartShip.delivery.resultPrice,
);

const selectMaxDividend = createSelector(
	state => state.cart.statusData.maxBonusDiscount,
	maxBonusDiscount => maxBonusDiscount,
);

const updateUserFormAsUser = createAction('UPDATE_USER_FORM_AS_USER', checked => (_, getState) => {
	const {
		cart: {
			statusData: { user: userData },
		},
	} = getState();

	if (checked) {
		return {
			name: mapValueFromServer(userData.name),
			phone: mapValueFromServer(userData.mobile),
			sameWithUser: true,
		};
	}

	return { name: '', phone: '', sameWithUser: false };
});

const updateInvoiceFormAsUser = createAction(
	'UPDATE_INVOICE_FORM_AS_USER',
	checked => (_, getState) => {
		const {
			cart: {
				statusData: { user: userData },
			},
		} = getState();

		if (checked) {
			return {
				zipCode: mapValueFromServer(userData.zip_code),
				address: mapValueFromServer(userData.address),
			};
		}

		return { zipCode: '', address: '' };
	},
);

const checkShipValue = createAction('CHECK_SHIP_VALUE', () => (dispatch, getState) => {
	const { delivery, payment, dividend } = selectCartShip(getState());

	if (isEmpty(delivery.type) || isEmpty(payment.select)) {
		dispatch(openWarningAlert('必填欄位未填寫'));
		return { paymentStep: 0 };
	}

	if (isExist(dividend.select)) {
		const maxCredit = selectMaxDividend(getState());

		if (isEmpty(dividend.value) || parseInt(dividend.value, 10) <= 0) {
			dispatch(openWarningAlert('請輸入紅利點數'));
			return { paymentStep: 0 };
		}

		if (parseInt(dividend.value, 10) > maxCredit) {
			dispatch(openWarningAlert('超過最高可折抵的紅利點數'));
			return { paymentStep: 0 };
		}
	}

	dispatch(getContact(delivery.type));
	dispatch(updateUserFormAsUser(true));

	return { paymentStep: 1 };
});

const selectUserForm = createSelector(
	state => state.cart.userForm,
	form => form,
);

const selectRecipientForm = createSelector(
	state => state.cart.recipientForm,
	form => form,
);

const checkUserValue = createAction('CHECK_USER_VALUE', () => (dispatch, getState) => {
	const userForm = selectUserForm(getState());
	const recipientForm = selectRecipientForm(getState());
	const delivery = selectDelivery(getState());

	if (
		isEmpty(userForm.name) ||
		isEmpty(userForm.phone) ||
		isEmpty(recipientForm.name) ||
		isEmpty(recipientForm.phone) ||
		(delivery.type !== 'SHOP' && (isEmpty(recipientForm.address) || isEmpty(recipientForm.zipCode)))
	) {
		dispatch(openWarningAlert('必填欄位未填寫'));
		return { paymentStep: 1 };
	}

	dispatch(updateInvoiceFormAsUser(true));

	return { paymentStep: 2 };
});

const selectInvoice = createSelector(
	state => state.cart.invoice,
	invoice => invoice,
);

const checkInvoiceValue = createAction('CHECK_INVOICE_VALUE', () => (dispatch, getState) => {
	const {
		cart: { invoice },
	} = getState();

	if (isEmpty(invoice.select)) {
		dispatch(openWarningAlert('必填欄位未填寫'));
		return { submit: false };
	}

	if (invoice.select === 'ECPAY' && (isEmpty(invoice.zipCode) || isEmpty(invoice.address))) {
		dispatch(openWarningAlert('必填欄位未填寫'));
		return { submit: false };
	}

	if (invoice.select === 'PAPER') {
		if (isEmpty(invoice.zipCode) || isEmpty(invoice.address)) {
			dispatch(openWarningAlert('必填欄位未填寫'));
			return { submit: false };
		}

		if (
			isExist(invoice.taxNumber) &&
			(invoice.taxNumber.length !== 8 || !/[0-9]{8}/.test(invoice.taxNumber))
		) {
			dispatch(openWarningAlert('統一編號格式錯誤，請重新輸入'));
			return { submit: false };
		}
	}
	if (invoice.select === 'MOBILE') {
		if (isEmpty(invoice.phoneSeriesNumber)) {
			dispatch(openWarningAlert('必填欄位未填寫'));
			return { submit: false };
		}

		if (
			invoice.phoneSeriesNumber.length !== 8 ||
			!/\/[A-Z0-9+-.]{7}/.test(invoice.phoneSeriesNumber)
		) {
			dispatch(openWarningAlert('載具格式錯誤，請重新輸入'));
			return { submit: false };
		}
	}
	if (invoice.select === 'MOICA') {
		if (isEmpty(invoice.certificate)) {
			dispatch(openWarningAlert('必填欄位未填寫'));
			return { submit: false };
		}

		if (invoice.certificate.length !== 16 || !/[a-zA-Z]{2}[0-9]{14}/.test(invoice.certificate)) {
			dispatch(openWarningAlert('載具格式錯誤，請重新輸入'));
			return { submit: false };
		}
	}

	if (invoice.select === 'DONATE') {
		if (isEmpty(invoice.donateNumber)) {
			dispatch(openWarningAlert('必填欄位未填寫'));
			return { submit: false };
		}

		if (invoice.donateNumber.length > 7 || !/[0-9]{3,7}/.test(invoice.donateNumber)) {
			dispatch(openWarningAlert('載具格式錯誤，請重新輸入'));
			return { submit: false };
		}
	}

	const { cart, product } = getState();

	// Need Cache not buying cart and product.
	const needCacheCartData = {};
	const needCacheProduct = {};
	const needCacheCartList = cart.list.filter(id => {
		if (!cart.data[id].checked) {
			needCacheCartData[id] = cart.data[id];
			needCacheProduct[cart.data[id].productId] = product.data[cart.data[id].productId];
			return true;
		}

		return false;
	});

	storage.setItem('cartData', JSON.stringify(needCacheCartData));
	storage.setItem('cartList', JSON.stringify(needCacheCartList));
	storage.setItem('productData', JSON.stringify(needCacheProduct));
	storage.setItem('cartAdditional', JSON.stringify({}));

	return { submit: true };
});

const updateUserForm = createAction('UPDATE_USER_FORM', (key, value) => ({ key, value }));

const updateRecipientForm = createAction('UPDATE_RECIPIENT_FORM', (key, value) => ({
	key,
	value,
}));

const updateRecipientFormAsUser = createAction(
	'UPDATE_RECIPIENT_FORM_AS_USER',
	checked => (_, getState) => {
		const {
			cart: {
				statusData: { user: userData },
			},
		} = getState();

		if (checked) {
			return {
				name: mapValueFromServer(userData.name),
				phone: mapValueFromServer(userData.mobile),
				zipCode: mapValueFromServer(userData.zip_code),
				address: mapValueFromServer(userData.address),
				sameWithUser: true,
			};
		}

		return { name: '', phone: '', zipCode: '', address: '', sameWithUser: false };
	},
);

const updateRecipientFormAsContact = createAction(
	'UPDATE_RECIPIENT_FORM_AS_CONTACT',
	contactId => (_, getState) => {
		const {
			contact: { data: contactData },
			cart: { contact },
		} = getState();

		if (contact.value !== contactId) {
			return {
				name: contactData[contactId].name,
				phone: contactData[contactId].phone,
				zipCode: contactData[contactId].zip_code,
				address: contactData[contactId].address,
				contactId,
			};
		}

		return {
			name: '',
			phone: '',
			zipCode: '',
			address: '',
			contactId: null,
		};
	},
);

const updateRemarks = createAction('UPDATE_REMARKS', value => value);

const updateInvoiceValue = createAction('UPDATE_INVOICE_VALUE', (key, value) => ({
	key,
	value,
}));

export const calculateTotalPriceForPromotion = createSelector(
	calculateTotalPrice,
	totalPrice => totalPrice,
);

export const calculateTotalPriceForPromotionEpics = (action$, state$) =>
	action$.pipe(
		filter(
			action =>
				action.type === 'RESET_CART_DATA' ||
				action.type === 'TOGGLE_PRODUCT_IN_CART' ||
				action.type === 'UPDATE_PRODUCT_COUNT' ||
				action.type === 'REMOVE_PRODUCT_FROM_CART' ||
				action.type === 'REMOVE_ADDITIONAL_FROM_CART',
		),
		debounceTime(300),
		switchMap(async () => {
			const {
				cart: { data: cartData, list, additional },
			} = state$.value;

			const defaultData = {
				description: '',
			};

			const additionalData = Object.values(additional)
				.filter(item => item.checked === true)
				.map(add => ({
					quantity: add.count,
					no: add.no,
					product_id: add.productId,
				}));

			const mainProductData = list
				.filter(cartId => cartData[cartId].checked)
				.map(cartId => ({
					quantity: cartData[cartId].count,
					no: cartData[cartId].no,
					product_id: cartData[cartId].productId,
				}));

			const checkData = mainProductData.concat(additionalData);

			const price = calculateTotalPriceForPromotion(state$.value);

			const { status, data } = await wrapFetch('cart/promotions', {
				method: 'POST',
				body: JSON.stringify({
					price,
					products: checkData,
				}),
			});
			if (status !== 200) {
				return defaultData;
			}

			return { type: 'CHECK_CART_PROMOTION_FULFILLED', payload: data };
		}),
	);

const cleanCartPromotion = createAction('CLEAN_CART_PROMOTION');

const addCouponInCart = createAction('ADD_COUPON_IN_CART', series => async (dispatch, getState) => {
	const {
		cart: { data: cartData, list, additional },
	} = getState();

	const productData = list
		.map(id => cartData[id])
		.filter(product => product.checked)
		.map(product => {
			const cartAdditional = Object.values(additional)
				.filter(checkAdd => checkAdd.checked && checkAdd.parentId === product.productId)
				.map(item => ({
					id: item.productId,
					quantity: item.count,
					no: item.no,
				}));

			return {
				increase_purchases: cartAdditional,
				no: product.no,
				quantity: product.count,
				product_id: parseInt(product.productId, 10),
			};
		});

	const { status, data, message } = await wrapAuthFetch(`cart/ec-coupons/${series}`, {
		method: 'POST',
		body: JSON.stringify({ products: productData }),
	});

	if (status !== 200) {
		return { data: {}, message };
	}

	const price = calculateTotalPriceForPromotion(getState());
	const promotion = selectPromotion(getState());
	const promotionPrice = promotion.discount.length > 0 ? promotion.discount[0].discount : 0;
	const totalPrice = price - promotionPrice;

	if (data.coupon.threshold > totalPrice) {
		dispatch(openWarningAlert('不足折抵金額，本次訂單無法使用'));
		return { data: {} };
	}

	dispatch(getCouponListInCart());
	dispatch(openNormalAlert('兌換成功'));

	dispatch(
		updateShipValue('coupon', {
			id: data.coupon.id,
			discount: data.coupon.discount,
			type: data.coupon.discount_type,
			products: data.coupon.restricted_product_ids,
		}),
	);

	return { data };
});

const mapHooksToStateByAddCouponInCart = createSelector(
	state => state.cart.couponList.message,
	cartCouponMessage => cartCouponMessage,
);

export const useAddCouponInCart = () =>
	useRedux(mapHooksToStateByAddCouponInCart, { addCouponInCart });

const reducer = {
	cart: handleActions(
		{
			ADD_PRODUCT_INTO_CART: (state, action) => ({
				...state,

				data: {
					...state.data,
					[action.payload.id]: action.payload,
				},
				list: state.list.includes(action.payload.id)
					? state.list
					: [...state.list, action.payload.id],
			}),

			REMOVE_PRODUCT_FROM_CART: (state, action) => {
				const newAdditional = {};
				Object.values(state.additional).forEach(item => {
					if (item.parentId !== action.payload.id) {
						newAdditional[`${item.id}`] = item;
					}
				});
				return {
					...state,

					list: state.list.filter(id => id !== action.payload.no),
					additional: newAdditional,
				};
			},

			UPDATE_PRODUCT_COUNT: (state, action) => ({
				...state,
				data: {
					...state.data,
					[action.payload.id]: {
						...state.data[action.payload.id],
						count: action.payload.count,
					},
				},
			}),

			TOGGLE_PRODUCT_IN_CART: (state, action) => ({
				...state,
				data: {
					...state.data,
					...action.payload.productData,
				},

				additional: { ...state.additional, ...action.payload.additional },
			}),
			ADD_ADDITIONAL_INTO_CART: (state, action) => ({
				...state,
				additional: {
					...state.additional,
					[action.payload.id]: action.payload,
				},
			}),

			REMOVE_ADDITIONAL_FROM_CART: (state, action) => {
				const newAdditional = state.additional;

				delete newAdditional[action.payload];

				return {
					...state,
					additional: {
						...newAdditional,
					},
				};
			},

			UPDATE_ADDITIONAL_COUNT: (state, action) => ({
				...state,
				additional: {
					...state.additional,
					[action.payload.id]: {
						...state.additional[action.payload.id],
						count: action.payload.count,
					},
				},
			}),

			TOGGLE_ADDITIONAL_IN_CART: (state, action) => ({
				...state,
				additional: {
					...state.additional,
					...action.payload.additional,
				},
			}),

			NEXT_CART_STEP: state => ({
				...state,

				step: state.step + 1,
			}),

			PREV_CART_STEP: state => ({
				...state,

				step: state.step - 1,
			}),

			SETUP_CART_STEUP: (state, action) => ({
				...state,

				step: action.payload.step,
			}),

			CHECK_CART_STATUS_FULFILLED: (state, action) => ({
				...state,

				statusData: action.payload,
			}),

			CHECK_CART_PROMOTION_FULFILLED: (state, action) => ({
				...state,
				promotion: action.payload,
			}),

			CLEAN_CART_PROMOTION: state => ({
				...state,
				promotion: [],
			}),

			ADD_COUPON_IN_CART_PENDING: state => ({
				...state,
				loading: true,
			}),

			ADD_COUPON_IN_CART_FULFILLED: (state, action) => ({
				...state,
				couponList: {
					...state.couponList,
					message: action.payload.message,
					loading: false,
				},
			}),

			GET_COUPON_LIST_IN_CART_FULFILLED: (state, action) => ({
				...state,
				couponList: {
					...state.couponList,
					coupon: action.payload,
				},
			}),
			CLEAN_CURRENT_COUPON_DATA: state => ({
				...state,
				coupon: {
					select: '',
					value: {
						id: 0,
						discount: 0,
						type: '',
					},
				},
				couponList: [],
			}),

			UPDATE_SHIP_SELECT: (state, action) => ({
				...state,

				[action.payload.key]: {
					...state[action.payload.key],
					select: action.payload.value,
				},
			}),

			CLEAN_SHIP_SELECT: state => ({
				...state,
				delivery: { select: -1 },
			}),

			UPDATE_SHIP_VALUE: (state, action) => ({
				...state,

				[action.payload.key]: {
					...state[action.payload.key],
					value: action.payload.value,
				},
			}),

			JUMP_ON_PAYMENT_STEP: (state, action) => ({
				...state,
				paymentStep: action.payload.paymentStep,
			}),

			CHECK_SHIP_VALUE: (state, action) => ({
				...state,

				paymentStep: action.payload.paymentStep,
			}),

			CHECK_USER_VALUE: (state, action) => ({
				...state,

				paymentStep: action.payload.paymentStep,
			}),

			CHECK_INVOICE_VALUE: (state, action) => ({
				...state,

				submit: action.payload.submit,
			}),

			UPDATE_USER_FORM: (state, action) => ({
				...state,

				userForm: {
					...state.userForm,
					[action.payload.key]: action.payload.value,
				},
			}),

			UPDATE_USER_FORM_AS_USER: (state, action) => ({
				...state,

				userForm: {
					...state.userForm,
					...action.payload,
				},
			}),

			UPDATE_RECIPIENT_FORM: (state, action) => ({
				...state,

				recipientForm: {
					...state.recipientForm,
					[action.payload.key]: action.payload.value,
				},
			}),

			UPDATE_RECIPIENT_FORM_AS_USER: (state, action) => ({
				...state,

				recipientForm: {
					...state.recipientForm,
					...action.payload,
				},
				contact: {
					value: null,
				},
			}),

			UPDATE_RECIPIENT_FORM_AS_CONTACT: (state, action) => ({
				...state,

				recipientForm: {
					...state.recipientForm,
					name: action.payload.name,
					phone: action.payload.phone,
					address: action.payload.address,
					zipCode: action.payload.zipCode,
					sameWithUser: false,
				},

				contact: {
					value: action.payload.contactId,
				},
			}),

			UPDATE_REMARKS: (state, action) => ({
				...state,

				remarks: action.payload,
			}),

			UPDATE_INVOICE_VALUE: (state, action) => ({
				...state,

				invoice: {
					...state.invoice,
					[action.payload.key]: action.payload.value,
				},
			}),

			UPDATE_INVOICE_FORM_AS_USER: (state, action) => ({
				...state,

				invoice: {
					...state.invoice,
					zipCode: action.payload.zipCode,
					address: action.payload.address,
				},
			}),

			RESET_CART_DATA: state => ({
				...state,

				delivery: {
					select: -1,
				},
				payment: {
					select: '',
					value: 0,
				},
				dividend: {
					select: '',
					value: 0,
				},
				coupon: {
					select: '',
					value: {
						id: 0,
						discount: 0,
						type: '',
					},
				},
				invoice: {
					select: '',
					zipCode: '',
					address: '',
					taxName: '',
					taxNumber: '',
					phoneSeriesNumber: '',
					certificate: '',
					donateNumber: '',
				},

				userForm: {
					name: '',
					phone: '',
					sameWithUser: false,
				},

				recipientForm: {
					name: '',
					phone: '',
					address: '',
					zipCode: '',
					sameWithUser: false,
				},

				remarks: '',

				contact: {
					value: null,
				},

				statusData: defaultStatusData,

				paymentStep: 0,

				submit: false,
			}),

			LOAD_CART_DATA_FROM_LOCALSTORAGE: (state, action) => ({
				...state,

				data: { ...state.data, ...action.payload.carData },
				list: action.payload.carList,
				additional: action.payload.additionalData,
			}),

			CLEAR_CART: state => ({
				...state,

				data: {},
				list: [],
			}),

			UPDATE_ADDITIONAL_PRODUCT: (state, action) => ({
				...state,
				additional: {
					...state.additional,
					[action.payload.id]: {
						...state.additional[action.payload.id],
						stockData: {
							...state.additional[action.payload.id].stockData,
							...action.payload.additionalData,
						},
					},
				},
			}),
		},
		{
			data: {},
			list: [],

			delivery: {
				select: -1,
			},
			payment: {
				select: '',
				value: 0,
			},
			dividend: {
				select: '',
				value: 0,
			},
			invoice: {
				select: '',
				zipCode: '',
				address: '',
				taxName: '',
				taxNumber: '',
				phoneSeriesNumber: '',
				certificate: '',
				donateNumber: '',
			},

			userForm: {
				name: '',
				phone: '',
				sameWithUser: false,
			},

			recipientForm: {
				name: '',
				phone: '',
				address: '',
				zipCode: '',
				sameWithUser: false,
			},

			reward: {
				select: null,
			},

			remarks: '',

			contact: {
				status: 'normal',
				value: null,
			},

			statusData: defaultStatusData,

			step: 0,

			paymentStep: 0,

			submit: false,

			additional: {},

			promotion: [],

			couponList: [],
		},
	),
};

const mapHooksToState = createSelector(
	state => state.cart.step,
	step => step,
);

export const useCartStep = () => useRedux(mapHooksToState, { nextCartStep, prevCartStep });

export const useCartProduct = () =>
	useRedux(selectCartProduct, {
		addProductIntoCart: addProductIntoCartProcess,
		removeProductFromCart,
		updateProductCount,
		toggleProductInCart,
	});

const selectCartAdditional = createSelector(
	state => state.cart.additional,
	additional => additional,
);

export const useCartAdditional = () =>
	useRedux(selectCartAdditional, {
		addAdditionalIntoCart: addAdditionalIntoCartProcess,
		removeAdditionalFromCart,
		updateAdditionalCount,
		toggleAdditionalInCart,
	});
export const useAdditionalTotalPrice = () => useRedux(calculateAdditionalPrice);
export const useCartTotalPrice = () => useRedux(calculateTotalPrice);

const selectCartData = createSelector(
	state => state.cart.data,
	state => state.cart.list,
	(cartData, cartList) => ({ cartData, cartList }),
);

export const useCartData = () => useRedux(selectCartData);

export const useAdditionalData = () => useRedux(selectAdditionalData);

const selectMaxReward = createSelector(
	state => state.cart.statusData.rewards,
	state => state.cart.statusData.items,
	calculatePricenForCumulativeBonus,
	(rewardsOptions, items, totalPrice) => {
		const reward = rewardsOptions.filter(
			item => item.price <= totalPrice && item.real_status === 'ONLINE',
		);
		const rewardFilter = Math.max(...reward.map(item => item.value));
		const rewardMax = reward.find(item => item.value === rewardFilter) || null;
		const creditableProduct = items.filter(item => item.is_creditable === 'ACTIVE');
		return creditableProduct.length ? rewardMax : null;
	},
);

export const useCartDiscountRulePrice = () => useRedux(getDiscountRulePrice);
export const useCartTotalPriceWithStep1 = () => useRedux(calculateTotalPriceWithStep1);
export const useCartTotalPriceForDelivery = () => useRedux(calculateTotalPriceForDelivery);
export const useCartTotalPriceWithStep2 = () => useRedux(calculateTotalPriceWithStep2);
export const useCartTotalPriceForPromotion = () => useRedux(calculateTotalPriceForPromotion);
export const useCartTotalPriceForBonus = () => useRedux(calculateTotalPriceForBonus);
export const useMaxReward = () => useRedux(selectMaxReward);

export const selectStatus = createSelector(
	state => state.cart.statusData,
	status => status,
);

export const useCartStatus = () => useRedux(selectStatus, { checkCartStatus });

export const useCartShip = () =>
	useRedux(selectCartShip, { updateShipSelect, updateShipValue, cleanShipSelect });

export const useCartMaxDividend = () => useRedux(selectMaxDividend);

const mapHooksToStateWithPayment = createSelector(
	state => state.cart.paymentStep,
	step => step,
);

export const usePaymentStep = () =>
	useRedux(mapHooksToStateWithPayment, {
		checkShipValue,
		checkUserValue,
		jumpOnPaymentStep,
	});

export const useUserForm = () => useRedux(selectUserForm, { updateUserForm, updateUserFormAsUser });

export const useRecipientForm = () =>
	useRedux(selectRecipientForm, { updateRecipientForm, updateRecipientFormAsUser });

const selectRemarks = createSelector(
	state => state.cart.remarks,
	remarks => remarks,
);

export const useRemarks = () => useRedux(selectRemarks, { updateRemarks });

export const useInvoice = () => useRedux(selectInvoice, { updateInvoiceValue });

const selectSubmit = createSelector(
	state => state.cart.submit,
	submit => submit,
);

export const usePaymentSubmit = () => useRedux(selectSubmit, { checkInvoiceValue });

const selectCartContact = createSelector(
	state => state.cart.contact,
	contact => contact,
);

export const useCartContact = () => useRedux(selectCartContact, { updateRecipientFormAsContact });

export const selectRewards = createSelector(
	state => state.cart.statusData.rewards,
	state => state.cart.reward,
	(rewardsOptions, reward) => ({ rewardsOptions, reward }),
);

export const useCartPromotion = () => useRedux(selectPromotion, { cleanCartPromotion });

export const useCartRewards = () => useRedux(selectRewards, { updateShipSelect });

const defaultCoupon = {
	id: 0,
	description: '',
	discount: 0,
	discount_type: '',
	series: '',
	threshold: 0,
	title: '',
};

const cleanCurrentCouponData = createAction('CLEAN_CURRENT_COUPON_DATA');

const getCurrentCouponData = createSelector(
	state => state.cart.couponList.coupon,
	state => state.cart.coupon,
	(couponList, couponSelected) => {
		if (couponList.data === null) {
			return defaultCoupon;
		}
		const result = couponList.data.find(item => item.id === couponSelected.value.id);
		return result;
	},
);

export const useCouponData = () => useRedux(getCurrentCouponData);

const selectCoupon = createSelector(
	state => state.cart.coupon,
	couponSelect => couponSelect,
);

const selectAvailableCoupons = createSelector(
	state => state.cart.couponList.coupon,
	calculateTotalPrice,
	selectPromotion,
	(coupon, totalPrice, promotion) => {
		const resultPromotion = promotion.discount.length > 0 ? promotion.discount[0].discount : 0;
		const totalAllPrice = totalPrice - resultPromotion;
		const availableCoupon = coupon
			? coupon.data.filter(item => item.threshold <= totalAllPrice)
			: [];
		return { availableCoupon };
	},
);

export const useAvailableCoupon = () => useRedux(selectAvailableCoupons);

export const useCoupon = () =>
	useRedux(selectCoupon, { updateShipSelect, updateShipValue, cleanCurrentCouponData });

export default { reducer };
